/**
 * Single delegated tracking plugin for generic UI-interaction GA4 events
 */

const BUTTON_SELECTOR = 'button, [role="button"], .v-btn, input[type="submit"], input[type="button"], input[type="reset"]'

/**
 * Some elements can be tracked without being a button.
 */
const TRACKABLE_SELECTOR = `${BUTTON_SELECTOR}, [data-track-id]`

function normalize(raw: string): string {
  return raw.trim().toLowerCase().replace(/[-\s.]+/g, '_')
}

function resolveIdentifier(el: Element, overrideAttr: string): string | null {
  const raw
    = el.getAttribute(overrideAttr)
      || el.getAttribute('data-test-id')
      || el.getAttribute('test-id')
      || el.id
      || ''

  return raw.trim() ? normalize(raw) : null
}

export default defineNuxtPlugin(() => {
  const gtm = useGtm()
  if (!gtm) return
  const logger = useLogger()

  const trackVideo = (videoEl: Element): void => {
    const videoLabel = resolveIdentifier(videoEl, 'data-video-label')
    if (!videoLabel) {
      // This log is for finding untracked video elements
      if (import.meta.dev)
        logger.warn('video element has no resolvable identifier (data-video-label/data-test-id/test-id/id)', { videoEl })
      return
    }
    const videoUrl = videoEl.getAttribute('data-video-url') ?? undefined
    gtm.trackEvent({
      event: 'click_video',
      video_label: videoLabel,
      ...(videoUrl !== undefined ? { video_url: videoUrl } : {}),
      page_section: usePageSection(),
    })
  }

  const handleClick = (event: Event): void => {
    const target = event.target as HTMLElement | null
    if (!target) return

    // added to skip tracking for certain elements, but currently disabled
    // if (target.closest('[data-track-skip]')) return

    const navEl = target.closest('[data-nav-id]')
    if (navEl) {
      const trackingId = resolveIdentifier(navEl, 'data-nav-id')
      if (!trackingId) {
        // This log is for finding untracked navigation elements
        if (import.meta.dev)
          logger.warn('navigation element has no resolvable identifier (data-nav-id/data-test-id/test-id/id)', { navEl })
        return
      }
      gtm.trackEvent({
        event: 'navigation_click',
        tracking_id: trackingId,
        page_section: usePageSection(),
      })
      return
    }

    // click_video takes precedence over button_click (video precedence before the button match).
    const videoEl = target.closest('[data-video-label]')
    if (videoEl) {
      trackVideo(videoEl)
      return
    }

    const button = target.closest(TRACKABLE_SELECTOR)
    if (!button) return

    // No resolvable identifier -> not tracked.
    const identifier = resolveIdentifier(button, 'data-track-id')
    if (!identifier) return

    gtm.trackEvent({
      event: 'button_click',
      tracking_id: identifier,
      page_section: usePageSection(),
    })
  }

  const handleChange = (event: Event): void => {
    const target = event.target as HTMLInputElement | null
    if (!target || target.type !== 'checkbox') return

    // The identifier lives on the native input (direct attr / `:input-attrs`) OR, far
    // more commonly, on the Vuetify `v-checkbox` root where fallthrough attrs land.
    const carrier = target.closest('[data-checkbox-id], [data-test-id], [test-id]')
    const checkboxId = carrier ? resolveIdentifier(carrier, 'data-checkbox-id') : null

    if (!checkboxId) {
      if (import.meta.dev)
        logger.warn('checkbox has no resolvable identifier (data-checkbox-id/data-test-id/test-id)', { target })
      return
    }

    const checkboxContext
      = target.getAttribute('data-checkbox-context')
        ?? target.closest('[data-checkbox-context]')?.getAttribute('data-checkbox-context')
        ?? undefined

    gtm.trackEvent({
      event: 'checkbox_interaction',
      checkbox_id: checkboxId,
      ...(checkboxContext !== undefined ? { checkbox_context: checkboxContext } : {}),
      checked: target.checked,
      page_section: usePageSection(),
    })
  }

  // Cross-origin iframes (YouTube, etc.) swallow clicks: the click fires inside the
  // iframe's own document and never reaches our delegated `document` listener. When the
  // user clicks into such an iframe the parent window loses focus and the iframe becomes
  // `document.activeElement`.
  //
  // WeakSet dedup: each iframe element is tracked at most once per page load, preventing
  // spurious duplicate events on alt-tab away or repeated re-focus of the same iframe.
  const trackedIframes = new WeakSet<HTMLIFrameElement>()

  const handleWindowBlur = (): void => {
    // `activeElement` is updated synchronously on blur, but defer once for browser parity.
    setTimeout(() => {
      const active = document.activeElement
      if (!(active instanceof HTMLIFrameElement)) return
      if (trackedIframes.has(active)) return
      const videoEl = active.closest('[data-video-label]')
      if (videoEl) {
        trackedIframes.add(active)
        trackVideo(videoEl)
      }
    }, 0)
  }

  useEventListener(document, 'click', handleClick, { passive: true })
  useEventListener(document, 'change', handleChange, { passive: true, capture: true })
  useEventListener(window, 'blur', handleWindowBlur)
})
